import { Component, inject } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { nonCanadianPostalValidator } from 'src/app/core/custom-validators/non-canadian-postal/non-canadian-postal.validator';
import { EditOnDemandPickupRequest } from 'src/app/core/models/operations/operations-on-demand-pickup.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { CONSTANTS } from './on-demand-pickup.constants';
import {
  CurrentPickupForm,
  NotesForm,
  OnDemandPickupData,
  SchedulingForm,
} from './on-demand-pickup.model';

@Component({
  selector: 'app-on-demand-pickup',
  templateUrl: './on-demand-pickup.component.html',
  styleUrls: ['./on-demand-pickup.component.scss'],
  standalone: false,
})
export class OnDemandPickupComponent {
  data: OnDemandPickupData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<OnDemandPickupComponent>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly commonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTANTS;
  readonly SCHEDULING_CONSTANTS = CONSTANTS.SCHEDULING;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  orderNumber = this.data?.data?.response.onDemandPickup?.requestShipperNo;
  schedulingForm!: FormGroup<SchedulingForm>;
  currentPickupForm!: FormGroup<CurrentPickupForm>;
  notesForm!: FormGroup<NotesForm>;
  validationSuccessful = false;
  // Whether user attempted address validation (prevents form.pristine from blocking Save)
  validationAttempted = false;

  ngOnInit(): void {
    this.schedulingForm = this.getInitialSchedulingForm();
    this.currentPickupForm = this.getInitialPickupForm();
    this.notesForm = this.getInitialNotesForm();
  }

  onSubmit() {
    // Force validation modal only if validation not yet attempted
    if (
      this.currentPickupForm.dirty &&
      !this.validationSuccessful &&
      !this.validationAttempted
    ) {
      this.validateModal();
      return;
    }

    const form = {
      ...this.schedulingForm.getRawValue(),
      ...this.currentPickupForm.getRawValue(),
      ...this.notesForm.getRawValue(),
    };

    const onDemandPickupRequest: EditOnDemandPickupRequest = {
      ondemandPickup: {
        orderNumber: this.orderNumber,
        readyTime: form.readyTime,
        closingTime: form.closingTime,
        commitTime: form.commitTime,
        updateTime: new Date().toString(),
        custName: form.customerName,
        contName: form.contactName ?? '',
        contact: form.contact ?? '',
        suite: form.suite ?? '',
        streetAddress: form.streetAddress ?? '',
        prefix: form.prefix ?? '',
        streetName: form.streetName,
        streetType: form.streetType ?? '',
        suffix: form.suffix ?? '',
        floor: form.floor ?? '',
        cityTown: form.cityTown ?? '',
        province: form.province,
        postalCode: form.postalCode,
        latitude: form.latitude,
        longitude: form.longitude,
        addlinfo: form.additionalInformation,
        status: this.data.status,
        IDOsuggestion: this.data.IDOsuggestion,
        packageType: this.data.packageType,
      },
    };
    this.dialogRef.close({
      request: onDemandPickupRequest,
      shipperPrn: this.data?.data?.response?.onDemandPickup?.requestShipperNo,
    });
  }

  validateModal() {
    this.commonUtils.openWarningModal({
      header: CONSTANTS.VALIDATE_MODAL.HEADER,
      body: CONSTANTS.VALIDATE_MODAL.BODY,
      confirmButtonText: CONSTANTS.VALIDATE_MODAL.CONFIRM_BUTTON,
      rejectButtonText: '',
    });
  }

  clickExit() {
    this.commonUtils.openExitModal(
      this.schedulingForm.dirty ||
        this.currentPickupForm.dirty ||
        this.notesForm.dirty,
    );
  }

  addressValidated(event: boolean) {
    // Update validation flags from child emit
    this.validationSuccessful = event;
    this.validationAttempted = event;
  }

  get disableButton() {
    // Enable Save when scheduling valid, or current pickup edited, or validation attempted, or notes valid
    const anyFormDirtyAndValid =
      (this.schedulingForm?.dirty && this.schedulingForm?.valid) ||
      this.currentPickupForm?.dirty ||
      this.validationAttempted ||
      (this.notesForm?.dirty && this.notesForm?.valid);

    return !anyFormDirtyAndValid;
  }

  private getInitialSchedulingForm() {
    const response = this.data?.data?.response;
    return this.fb.group<SchedulingForm>({
      readyTime: this.fb.control(
        response?.onDemandPickup?.readyTime,
        Validators.minLength(5),
      ),
      commitTime: this.fb.control(
        response?.onDemandPickup?.commitTime,
        Validators.minLength(5),
      ),
      closingTime: this.fb.control(
        response?.onDemandPickup?.closingTime,
        Validators.minLength(5),
      ),
    });
  }

  private getInitialPickupForm() {
    const isValidatorOptional = this.commonUtils.isStateProvinceOptional(
      this.data.countryCode,
      this.data.locationGroup,
      this.data.locationCode,
    );

    const response = this.data?.data?.response;
    return this.fb.group<CurrentPickupForm>({
      customerName: this.fb.control(
        response.currentPickupAddress.customerName,
        Validators.required,
      ),
      contactName: this.fb.control(response.currentPickupAddress.contactName),
      contact: this.fb.control(response.currentPickupAddress.contact),
      streetAddress: this.fb.control(
        response.currentPickupAddress.streetAddress,
      ),
      prefix: this.fb.control(response.currentPickupAddress.prefix),
      streetName: this.fb.control(
        response.currentPickupAddress.streetName,
        isValidatorOptional ? Validators.nullValidator : Validators.required,
      ),
      suffix: this.fb.control(response.currentPickupAddress.suffix),
      streetType: this.fb.control(response.currentPickupAddress.streetType),
      suite: this.fb.control(response.currentPickupAddress.suite),
      floor: this.fb.control(response.currentPickupAddress.floor),
      cityTown: this.fb.control(response.currentPickupAddress.cityTown),
      province: this.fb.control(
        response.currentPickupAddress.province,
        isValidatorOptional ? Validators.nullValidator : Validators.required,
      ),
      postalCode: this.fb.control(
        response.currentPickupAddress.postalCode,
        Validators.compose([
          Validators.required,
          nonCanadianPostalValidator(this.data.countryCode),
        ]),
      ),
      latitude: this.fb.control(response.currentPickupAddress.latitude),
      longitude: this.fb.control(response.currentPickupAddress.longitude),
    });
  }

  private getInitialNotesForm() {
    const response = this.data?.data?.response;
    return this.fb.group<NotesForm>({
      additionalInformation: this.fb.control(response.additionalInformation),
      driverReply: this.fb.control({
        value: response.driverReply,
        disabled: true,
      }),
      sscDescription: this.fb.control({
        value: response.sscDescription,
        disabled: true,
      }),
    });
  }
}
